import React from "react";

import isEmpty from "lodash/isEmpty";
import { useDispatch, useSelector } from "react-redux";

import {
  addBidderRegistration,
  addSaleLotWithPens,
  deleteSaleLot,
  patchSaleLot,
} from "actions";

import { addAuctionPen } from "actions/auctionPens";

import DetailedSaleLotModal from "components/DetailedSaleLotModal";

import { SaleLotModalSection } from "constants/navigation";
import { SaleType } from "constants/sale";

import { getDollarPriceStringFromCents } from "lib";

import {
  closeAllHashModals,
  openEditSaleLotModal,
  returnToLocation,
} from "lib/navigation";
import { generateListingId } from "lib/saleLot";

import {
  currentSaleSelector,
  getConsignmentFromIdOrSaleLotId,
  getExportSitesByDeploymentSaleId,
  getIsSaleyardAdmin,
  getMinimalFormattedSaleLotById,
  getNestedConsignments,
  getPrimaryAgency,
  getSaleLotsBySale,
  getSpeciesAttributeOptions,
  getSaleLotById,
  getPreviousSaleLotByConsignmentId,
} from "selectors";
import { resolveCopiedOrPreviousSaleLotValues } from "lib/saleLot/resolveCopiedOrPreviousSaleLotValues";

// TODO remove this component and place all of the logic directly into the
//  selector
const SaleLot = ({
  action,
  closeSelf,
  saleLotId,
  consignmentId,
  copyFrom,
  section,
  changeSection,
  returnTo,
  onBeforeClose,
}) => {
  const dispatch = useDispatch();

  const nestedConsignments = useSelector(getNestedConsignments);
  const allSaleLots = useSelector(getSaleLotsBySale);
  const copiedSaleLot = useSelector(getSaleLotById(copyFrom));
  const previousSaleLot = useSelector(
    getPreviousSaleLotByConsignmentId(consignmentId),
  );
  const speciesAttributes = useSelector(getSpeciesAttributeOptions);
  const saleLot = useSelector(state =>
    getMinimalFormattedSaleLotById(state, { saleLotId }),
  );
  const sale = useSelector(currentSaleSelector);
  const primaryAgency = useSelector(getPrimaryAgency);
  const isSaleyardAdmin = useSelector(getIsSaleyardAdmin);

  const consignment = getConsignmentFromIdOrSaleLotId(
    nestedConsignments,
    allSaleLots,
    { saleLotId: saleLotId || copyFrom, consignmentId },
  );

  const activeExportSites = useSelector(
    getExportSitesByDeploymentSaleId(consignment.deployment_sale),
  );

  const defaultPricingType = sale.pricing_type_id;

  function onClose() {
    if (returnTo) {
      returnToLocation(returnTo);
    } else {
      closeAllHashModals();
    }
  }

  const [refreshKey, setRefreshKey] = React.useState(
    saleLot.listingId ? saleLot.listingId : generateListingId(),
  );

  function onAfterClose(newSaleLotId, createAnother) {
    if (createAnother) {
      // When create another is ticked we want to get a new key to force a remount
      setRefreshKey(generateListingId());
      // use the copy draft functionality to show a new form
      openEditSaleLotModal(
        null,
        SaleLotModalSection.GENERAL,
        null,
        consignmentId,
        newSaleLotId,
        returnTo,
      );
    } else if (
      newSaleLotId &&
      isEmpty(saleLot) &&
      sale.sale_type === SaleType.CLEARING
    ) {
      // Keep it open for clearing sales.
      openEditSaleLotModal(
        newSaleLotId,
        SaleLotModalSection.GENERAL,
        null,
        consignmentId,
        null,
        returnTo,
      );
    }
  }

  const copiedOrPreviousValues = resolveCopiedOrPreviousSaleLotValues({
    isNew: !saleLotId,
    saleLot,
    copiedSaleLot,
    previousSaleLot,
    consignment,
    speciesId: sale.species_id,
  });

  // Add extra info to the initial values
  const initialValues = {
    // Set quantity before getting saleLot so if quantity is undefined in salelots
    // it will be 1, else, it will be the saleLot.quantity
    quantity: sale.sale_type === SaleType.CLEARING ? 1 : undefined,
    ...saleLot,
    ...copiedOrPreviousValues,
    cents_per_kg: saleLot.unit_price,
    price_per_head: getDollarPriceStringFromCents(
      saleLot.total_price_cents / saleLot.quantity,
    ),
    listingId: saleLot.listingId ? saleLot.listingId : generateListingId(),
    price_gross: getDollarPriceStringFromCents(saleLot.total_price_cents),
    pricing_type_id:
      saleLot.pricing_type_id ||
      copiedOrPreviousValues.pricing_type_id ||
      defaultPricingType,
    createAnother: Boolean(copiedSaleLot),
    auction_pen: saleLot.auction_pen || {},
    deliveryPen: saleLot.deliveryPen || {},
    pickupAddress: saleLot.pickupAddress || null,
    exportSites: saleLot.exportSites || activeExportSites,
  };

  // Set the agency in the fields if this is an admin - it will be needed
  // if we need to add the auction pen.
  if (isSaleyardAdmin) {
    initialValues.agency_id = consignment.agencyId;
  }

  const handleAddAuctionPen = (auctionPen, tempId) =>
    dispatch(addAuctionPen(auctionPen, tempId, [saleLotId]));

  const handleDeleteSaleLot = () => dispatch(deleteSaleLot(saleLot));

  const handlePatchSaleLot = (...args) => dispatch(patchSaleLot(...args));
  const handleAddSaleLotWithPens = (...args) =>
    dispatch(addSaleLotWithPens(...args));
  const handleAddBidderRegistration = (...args) =>
    dispatch(addBidderRegistration(...args));

  return (
    <DetailedSaleLotModal
      closeSelf={closeSelf || onClose}
      consignment={consignment}
      section={section}
      showCreateAnother={!saleLotId}
      changeSection={changeSection}
      action={action}
      speciesAttributes={speciesAttributes}
      initialValues={initialValues}
      consignmentId={consignmentId}
      patchSaleLot={handlePatchSaleLot}
      addSaleLotWithPens={handleAddSaleLotWithPens}
      addBidderRegistration={handleAddBidderRegistration}
      addAuctionPen={handleAddAuctionPen}
      saleLots={allSaleLots}
      deleteSaleLot={handleDeleteSaleLot}
      primaryAgency={primaryAgency}
      onBeforeClose={onBeforeClose}
      onAfterClose={onAfterClose}
      saleLotId={saleLotId}
      sale={sale}
      key={refreshKey}
    />
  );
};

export default SaleLot;
